import { FC, ComponentType, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  IconGasStation,
  IconChartLine,
  IconTrophy,
  IconCoin,
  IconUsers,
  IconMap2,
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons-react";
import {
  Box,
  Heading,
  Text,
  Flex,
  SimpleGrid,
  Icon,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
} from "@chakra-ui/react";
import { usePageTitle } from "@Hooks/usePageTitle";
import { useAcesso } from "@Hooks/useAcesso";
import { Button } from "@Components/Button";
import { FiltroBar } from "@Components/FiltroBar";
import { DataTable } from "@Components/DataTable";
import { AcessoNegadoModal } from "@Components/AcessoNegadoModal";
import { formatCurrency, formatDate } from "@Utils/index";
import {
  getRelatorioPostosForaRede,
  getRelatorioPrometidoVsAtual,
  getRelatorioRanking,
  getRelatorioEconomia,
  getRelatorioProdutividade,
  getRelatorioCobertura,
  RelatorioEscopo,
} from "@Api/estudo";

const num = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 0 });

// Avisos de escopo (aviso do backend + "só os seus dados" quando não é ver-todos).
const EscopoAvisos: FC<{ escopo?: RelatorioEscopo }> = ({ escopo }) => {
  if (!escopo) return null;
  return (
    <>
      {escopo.aviso && (
        <Alert status="warning" borderRadius="8px" mb="12px" fontSize="sm">
          <AlertIcon />
          {escopo.aviso}
        </Alert>
      )}
      {escopo.verTodos === false && (
        <Alert status="info" borderRadius="8px" mb="12px" fontSize="sm">
          <AlertIcon />
          Exibindo apenas os seus dados. Para ver de todos os consultores é preciso o
          papel "ver todos os estudos".
        </Alert>
      )}
    </>
  );
};

const Estado: FC<{ isLoading: boolean; isError: boolean; vazio: boolean }> = ({
  isLoading,
  isError,
  vazio,
}) => {
  if (isLoading)
    return (
      <Flex align="center" justify="center" py={12} gap={3}>
        <Spinner color="primary.light.base" size="sm" />
        <Text color="neutrals.text.sub-600" fontFamily="body">
          Carregando…
        </Text>
      </Flex>
    );
  if (isError)
    return (
      <Text py={10} color="neutrals.text.sub-600" fontFamily="body" fontSize="sm">
        Não foi possível carregar o relatório. Tente novamente em instantes.
      </Text>
    );
  if (vazio)
    return (
      <Text py={10} color="neutrals.text.sub-600" fontFamily="body" fontSize="sm">
        Nenhum resultado para os filtros atuais.
      </Text>
    );
  return null;
};

// ── Relatório 1: Postos fora da rede credenciada ──
const RelPostosForaRede: FC = () => {
  const [origem, setOrigem] = useState<"ambos" | "prospect" | "cliente">("ambos");
  const [periodoDias, setPeriodoDias] = useState(90);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["rel-postos-fora", origem, periodoDias],
    queryFn: () => getRelatorioPostosForaRede({ origem, periodoDias }),
    placeholderData: (prev) => prev,
  });

  const items = data?.items ?? [];

  return (
    <Box>
      <FiltroBar>
        <Select
          maxW="240px"
          aria-label="Origem"
          value={origem}
          onChange={(e) => setOrigem(e.target.value as typeof origem)}
        >
          <option value="ambos">Origem: ambos</option>
          <option value="prospect">Origem: oportunidade (prospects)</option>
          <option value="cliente">Origem: vazamento (clientes)</option>
        </Select>
        <Select
          maxW="200px"
          aria-label="Período"
          value={periodoDias}
          onChange={(e) => setPeriodoDias(Number(e.target.value))}
        >
          <option value={30}>Últimos 30 dias</option>
          <option value={90}>Últimos 90 dias</option>
          <option value={180}>Últimos 180 dias</option>
          <option value={365}>Últimos 365 dias</option>
        </Select>
      </FiltroBar>

      <EscopoAvisos escopo={data?.escopo} />
      <Estado isLoading={isLoading} isError={isError} vazio={items.length === 0} />

      {items.length > 0 && (
        <DataTable>
          <Table variant="compact">
            <Thead>
              <Tr>
                <Th>Posto (CNPJ)</Th>
                <Th>Cidade/UF</Th>
                <Th>Origem</Th>
                <Th isNumeric>Nº abast.</Th>
                <Th isNumeric>Litros</Th>
                <Th isNumeric>Valor</Th>
                <Th>Período</Th>
                <Th isNumeric>Nº empresas</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((r, i) => (
                <Tr key={`${r.postoCnpj}-${r.origem}-${i}`}>
                  <Td>{r.postoCnpj}</Td>
                  <Td>
                    {r.cidade ?? "—"}
                    {r.uf ? `/${r.uf}` : ""}
                  </Td>
                  <Td>
                    <Badge colorScheme={r.origem === "cliente" ? "purple" : "blue"}>
                      {r.origem}
                    </Badge>
                  </Td>
                  <Td isNumeric>{num(r.nAbastecimentos)}</Td>
                  <Td isNumeric>{num(r.litros)} L</Td>
                  <Td isNumeric>{formatCurrency(r.valor)}</Td>
                  <Td>
                    {formatDate(r.dataMin)} – {formatDate(r.dataMax)}
                  </Td>
                  <Td isNumeric>{num(r.nEmpresas)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      )}
    </Box>
  );
};

// ── Relatório 2: Volume prometido vs atual ──
const RelPrometidoVsAtual: FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["rel-prometido-vs-atual"],
    queryFn: () => getRelatorioPrometidoVsAtual(),
    placeholderData: (prev) => prev,
  });
  const items = data?.items ?? [];

  return (
    <Box>
      <EscopoAvisos escopo={data?.escopo} />
      <Estado isLoading={isLoading} isError={isError} vazio={items.length === 0} />

      {items.length > 0 && (
        <DataTable>
          <Table variant="compact">
            <Thead>
              <Tr>
                <Th>Empresa</Th>
                <Th>CNPJ</Th>
                <Th isNumeric>Prometido (L/mês)</Th>
                <Th isNumeric>Atual (L/mês)</Th>
                <Th isNumeric>Δ (L/mês)</Th>
                <Th isNumeric>Δ %</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((r) => (
                <Tr key={r.cnpj}>
                  <Td>{r.empresa ?? "—"}</Td>
                  <Td>{r.cnpj}</Td>
                  <Td isNumeric>{num(r.litrosMesPrometido)}</Td>
                  <Td isNumeric>{num(r.litrosMesAtual)}</Td>
                  <Td isNumeric color={r.delta < 0 ? "states.error.base" : "states.success.base"}>
                    {num(r.delta)}
                  </Td>
                  <Td isNumeric color={r.delta < 0 ? "states.error.base" : "states.success.base"}>
                    {r.deltaPct == null ? "—" : `${r.deltaPct.toFixed(1)}%`}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      )}
    </Box>
  );
};

// ── P2 ──
const RelRanking: FC = () => {
  const [periodoDias, setPeriodoDias] = useState(180);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["rel-ranking", periodoDias],
    queryFn: () => getRelatorioRanking({ periodoDias }),
    placeholderData: (prev) => prev,
  });
  const items = data?.items ?? [];
  return (
    <Box>
      <FiltroBar>
        <Select maxW="200px" aria-label="Período" value={periodoDias} onChange={(e) => setPeriodoDias(Number(e.target.value))}>
          <option value={90}>Últimos 90 dias</option>
          <option value={180}>Últimos 180 dias</option>
          <option value={365}>Últimos 365 dias</option>
        </Select>
      </FiltroBar>
      <EscopoAvisos escopo={data?.escopo} />
      <Estado isLoading={isLoading} isError={isError} vazio={items.length === 0} />
      {items.length > 0 && (
        <DataTable>
          <Table variant="compact">
            <Thead><Tr><Th>#</Th><Th>Posto (CNPJ)</Th><Th>Cidade/UF</Th><Th isNumeric>Nº abast.</Th><Th isNumeric>Litros</Th><Th isNumeric>Valor</Th><Th isNumeric>Nº empresas</Th></Tr></Thead>
            <Tbody>
              {items.map((r, i) => (
                <Tr key={r.postoCnpj}>
                  <Td>{i + 1}</Td>
                  <Td>{r.postoCnpj}</Td>
                  <Td>{r.cidade ?? "—"}{r.uf ? `/${r.uf}` : ""}</Td>
                  <Td isNumeric>{num(r.nAbastecimentos)}</Td>
                  <Td isNumeric>{num(r.litros)} L</Td>
                  <Td isNumeric>{formatCurrency(r.valor)}</Td>
                  <Td isNumeric>{num(r.nEmpresas)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      )}
    </Box>
  );
};

const RelEconomia: FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["rel-economia"],
    queryFn: () => getRelatorioEconomia(),
    placeholderData: (prev) => prev,
  });
  const items = data?.items ?? [];
  return (
    <Box>
      <EscopoAvisos escopo={data?.escopo} />
      <Estado isLoading={isLoading} isError={isError} vazio={items.length === 0} />
      {items.length > 0 && (
        <DataTable>
          <Table variant="compact">
            <Thead><Tr><Th>Empresa</Th><Th>CNPJ</Th><Th isNumeric>Prometida (R$/mês)</Th><Th isNumeric>Realizada (R$/mês)</Th><Th isNumeric>Δ</Th><Th isNumeric>Δ %</Th></Tr></Thead>
            <Tbody>
              {items.map((r) => (
                <Tr key={r.cnpj}>
                  <Td>{r.empresa ?? "—"}</Td>
                  <Td>{r.cnpj}</Td>
                  <Td isNumeric>{formatCurrency(r.economiaMesPrometida)}</Td>
                  <Td isNumeric>{formatCurrency(r.economiaMesRealizada)}</Td>
                  <Td isNumeric color={r.delta < 0 ? "states.error.base" : "states.success.base"}>{formatCurrency(r.delta)}</Td>
                  <Td isNumeric color={r.delta < 0 ? "states.error.base" : "states.success.base"}>{r.deltaPct == null ? "—" : `${r.deltaPct.toFixed(1)}%`}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      )}
    </Box>
  );
};

const RelProdutividade: FC = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["rel-produtividade"],
    queryFn: () => getRelatorioProdutividade(),
    placeholderData: (prev) => prev,
  });
  const items = data?.items ?? [];
  return (
    <Box>
      <EscopoAvisos escopo={data?.escopo} />
      <Estado isLoading={isLoading} isError={isError} vazio={items.length === 0} />
      {items.length > 0 && (
        <DataTable>
          <Table variant="compact">
            <Thead><Tr><Th>Consultor</Th><Th isNumeric>Nº estudos</Th><Th isNumeric>Litros analisados</Th><Th isNumeric>Conversões</Th></Tr></Thead>
            <Tbody>
              {items.map((r) => (
                <Tr key={r.consultor}>
                  <Td>{r.consultor}</Td>
                  <Td isNumeric>{num(r.nEstudos)}</Td>
                  <Td isNumeric>{num(r.litrosAnalisados)} L</Td>
                  <Td isNumeric>{num(r.nConversoes)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      )}
    </Box>
  );
};

const RelCobertura: FC = () => {
  const [periodoDias, setPeriodoDias] = useState(180);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["rel-cobertura", periodoDias],
    queryFn: () => getRelatorioCobertura({ periodoDias }),
    placeholderData: (prev) => prev,
  });
  const items = data?.items ?? [];
  return (
    <Box>
      <FiltroBar>
        <Select maxW="200px" aria-label="Período" value={periodoDias} onChange={(e) => setPeriodoDias(Number(e.target.value))}>
          <option value={90}>Últimos 90 dias</option>
          <option value={180}>Últimos 180 dias</option>
          <option value={365}>Últimos 365 dias</option>
        </Select>
      </FiltroBar>
      <EscopoAvisos escopo={data?.escopo} />
      <Estado isLoading={isLoading} isError={isError} vazio={items.length === 0} />
      {items.length > 0 && (
        <DataTable>
          <Table variant="compact">
            <Thead><Tr><Th>UF</Th><Th isNumeric>Litros total</Th><Th isNumeric>Cobertos (rede)</Th><Th isNumeric>Fora</Th><Th isNumeric>Cobertura</Th><Th isNumeric>Nº postos</Th></Tr></Thead>
            <Tbody>
              {items.map((r) => (
                <Tr key={r.uf ?? "sem-uf"}>
                  <Td>{r.uf ?? "sem UF"}</Td>
                  <Td isNumeric>{num(r.litrosTotal)} L</Td>
                  <Td isNumeric>{num(r.litrosCobertos)} L</Td>
                  <Td isNumeric>{num(r.litrosFora)} L</Td>
                  <Td isNumeric>{r.coberturaPct == null ? "—" : `${r.coberturaPct.toFixed(1)}%`}</Td>
                  <Td isNumeric>{num(r.nPostos)}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </DataTable>
      )}
    </Box>
  );
};

// Fonte única dos relatórios (usada na home em cards, no detalhe por slug e no submenu
// da sidebar). `icon` é um componente do @tabler/icons-react.
export interface RelatorioDef {
  key: string;
  label: string;
  descricao: string;
  icon: ComponentType<{ size?: number | string }>;
  comp: FC;
}

export const RELATORIOS: RelatorioDef[] = [
  { key: "postos-fora", label: "Postos fora da rede", icon: IconGasStation, comp: RelPostosForaRede, descricao: "Postos não credenciados com abastecimento — oportunidade e vazamento." },
  { key: "prometido-atual", label: "Volume prometido vs atual", icon: IconChartLine, comp: RelPrometidoVsAtual, descricao: "Para prospects convertidos: litros/mês projetado vs. realizado." },
  { key: "ranking", label: "Ranking de credenciamento", icon: IconTrophy, comp: RelRanking, descricao: "Postos fora da rede por volume — quais credenciar primeiro." },
  { key: "economia", label: "Economia prometida vs realizada", icon: IconCoin, comp: RelEconomia, descricao: "Economia mensal do estudo do prospect vs. do cliente convertido." },
  { key: "produtividade", label: "Produtividade por consultor", icon: IconUsers, comp: RelProdutividade, descricao: "Estudos, litros analisados e conversões por consultor." },
  { key: "cobertura", label: "Cobertura por UF", icon: IconMap2, comp: RelCobertura, descricao: "Litros cobertos pela rede vs. fora, por estado." },
];

// Card de um relatório na home (modelo Cardan: ícone rosé, título, descrição, seta).
const RelatorioCard: FC<{ def: RelatorioDef; onClick: () => void }> = ({ def, onClick }) => (
  <Box
    as="button"
    type="button"
    onClick={onClick}
    textAlign="left"
    position="relative"
    bg="neutrals.surface.surface-0"
    border="1px solid"
    borderColor="neutrals.stroke.soft-200"
    borderRadius="12px"
    p="20px"
    transition="all 0.15s"
    _hover={{ borderColor: "primary.light.base", boxShadow: "sm" }}
  >
    <Icon
      as={IconArrowRight}
      boxSize={4}
      color="neutrals.icon.soft-400"
      position="absolute"
      top="20px"
      right="20px"
    />
    <Flex align="center" justify="center" boxSize="44px" borderRadius="10px" bg="primary.light.alpha-10" mb="14px">
      <Icon as={def.icon} boxSize={5} color="primary.light.base" />
    </Flex>
    <Text fontSize="15px" fontWeight="700" fontFamily="heading" color="neutrals.text.strong-950">
      {def.label}
    </Text>
    <Text mt="6px" fontSize="13px" color="neutrals.text.sub-600" fontFamily="body" pr="16px">
      {def.descricao}
    </Text>
  </Box>
);

export const RelatoriosPage: FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const { canRelatorios } = useAcesso();
  const atual = slug ? RELATORIOS.find((r) => r.key === slug) : null;
  usePageTitle(atual ? atual.label : "Relatórios");

  if (!canRelatorios) {
    return (
      <AcessoNegadoModal
        isOpen
        onClose={() => navigate("/inicio")}
        titulo="Sem acesso aos relatórios"
        mensagem="Sua conta não tem o papel necessário para ver os relatórios. Procure o seu gestor para solicitar o acesso."
        botaoLabel="Voltar"
      />
    );
  }

  // Detalhe de um relatório (rota /relatorios/:slug).
  if (slug) {
    if (!atual) {
      return (
        <Box>
          <Button size="sm" colorSchema="neutral" variant="ghost" leftIcon={<Icon as={IconArrowLeft} boxSize={4} />} onClick={() => navigate("/relatorios")}>
            Relatórios
          </Button>
          <Text mt="10px" color="neutrals.text.sub-600" fontFamily="body">Relatório não encontrado.</Text>
        </Box>
      );
    }
    const C = atual.comp;
    return (
      <Box>
        <Heading fontSize="20px" fontFamily="heading" color="neutrals.text.strong-950">
          {atual.label}
        </Heading>
        <Text mt="4px" mb="20px" fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
          {atual.descricao}
        </Text>
        <C />
      </Box>
    );
  }

  // Home (rota /relatorios): grid de cards.
  return (
    <Box>
      <Heading fontSize="24px" fontFamily="heading" color="neutrals.text.strong-950">
        Relatórios
      </Heading>
      <Text mt="4px" mb="24px" fontSize="sm" color="neutrals.text.sub-600" fontFamily="body">
        Selecione um relatório para continuar.
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing="20px" maxW="1120px">
        {RELATORIOS.map((r) => (
          <RelatorioCard key={r.key} def={r} onClick={() => navigate(`/relatorios/${r.key}`)} />
        ))}
      </SimpleGrid>
    </Box>
  );
};
